import React, { useState } from "react";
import axios from "axios";
import Head from "next/head";
import styles from "../../signature.module.css";
import { FaFileZipper } from "react-icons/fa6";
import { BASE_URL_POST } from "../../../../constants/constants";
import SignaturePopup from "../../../../components/SignaturePopup";

import UploadFile from "@components/PdfTools/UploadFile";
import Steps from "@components/PdfTools/Steps";
import WhyChooseBolosign from "@components/PdfTools/WhyChooseBolosign";
import PricingCards from "@components/PdfTools/PricingCards";
import TryBolosign from "@components/PdfTools/TryBolosign";
import OtherPdfTools from "@components/PdfTools/OtherPdfTools";
import FaqCard from "@components/Cards/FaqCard";
import { LuUpload } from "react-icons/lu";
import { LuDownload } from "react-icons/lu";
import { FaCompressArrowsAlt } from "react-icons/fa";

const resolutions = [72, 100, 144, 200, 300, 720];

const steps = [
  {
    icon: <LuUpload />,
    title: "Upload Your File",
    description:
      "Drag and Drop your file into the compressor or simply click the upload button and select a document from your local storage.",
  },
  {
    icon: <FaCompressArrowsAlt />,
    title: "Compress your file",
    description:
      "Click on 'Compress' to initiate the PDF compression process. Our tool ensures that your PDF is compressed without compromising on quality.",
  },
  {
    icon: <LuDownload />,
    title: "Download PDF",
    description:
      "Once the PDF compression is complete, download your reduced PDF files from your browser.",
  },
];

const reasons = [
  {
    image: "drag_and_drop_upload.png",
    title: "Drag-and-drop upload",
    description:
      "Simply drag the file from your device to our Compress PDF tool to start the process instantly.",
  },
  {
    image: "file_deletion_policy.png",
    title: "File Deletion Policy",
    description:
      "We prioritize your privacy. All uploaded files are automatically deleted after conversion.",
  },
  {
    image: "cross_platform_compatibility.png",
    title: "Cross-Platform Compatibility",
    description:
      "Access our tool from any device, whether you're using a desktop, tablet, or smartphone.",
  },
  {
    image: "secure_encryption.png",
    title: "Secure encryption",
    description:
      "We use advanced encryption to ensure your files remain secure during the PDF compression process.",
  },
];

const faqData = [
  {
    faqQuestion: "How do I reduce the size of a PDF file?",
    faqAnswer:
      "Use our PDF compress tool by uploading your file and clicking ‘Compress’.",
  },
  {
    faqQuestion: "What does a PDF compressor mean?",
    faqAnswer:
      "A PDF compressor reduces the file size of a PDF without compromising its quality.",
  },
  {
    faqQuestion: "How do I reduce PDF to 100kb?",
    faqAnswer:
      "Upload your file to our PDF compress tool and select the desired file size.",
  },
  {
    faqQuestion: "Is it bad to compress PDFs?",
    faqAnswer:
      "No, compressing PDFs helps save storage space and makes them easier to share.",
  },
];

function CompressPdf() {
  const [file, setFile] = useState(null);
  const [resolution, setResolution] = useState(72);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const [signaturePopup, setSignaturePopup] = useState(false);

  const scrollToPreview = () => {
    const element = document.querySelector(".resolution-field");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleFileUpload = (files) => {
    setFile(files[0]);
    setError("");
    if (files) {
      scrollToPreview();
    }
  };

  const compressPdf = async () => {
    setError("");
    if (!file) {
      setError("Please choose a file");
      return;
    }

    if (file.name.split(".").pop() !== "pdf") {
      setError("Please choose a pdf file");
      return;
    }

    if (!resolution || resolution === NaN) {
      setResolution(100);
    }

    if (file.size > 30000000) {
      setError("File Size is too large(>30MB)");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("resolution", resolution);
    try {
      setDownloading(true);
      const response = await axios.post(
        `${BASE_URL_POST}compressPdf`,
        formData,
        {
          responseType: "blob",
        },
      );
      // console.log(response)
      downloadFile(response.data);
    } catch (error) {
      setDownloading(false);
      if (error.response.data.type === "text/html") {
        const blobReader = new FileReader();
        blobReader.addEventListener("loadend", () => {
          setError(blobReader.result);
        });
        blobReader.readAsText(error.response.data);
        return;
      }
      setError(error.response.data);
    }
  };

  const downloadFile = (blob) => {
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = "compressed.pdf";
    link.click();
    setDownloading(false);
    setTimeout(() => {
      setSignaturePopup(true);
    }, 2000);
  };

  return (
    <div className="w-full mt-20 sm:mt-24">
      <Head>
        <meta charSet="utf-8" />
        <title>Compress PDF Tool - BoloForms</title>
        <meta
          name="description"
          content="Compress PDF Tool allows you to reduce the size of your PDF documents without losing quality."
        />
        <meta
          name="keywords"
          content="compress pdf, compress pdf online, compress pdf file, compress pdf document, compress pdf file online, compress pdf file free, compress pdf free, compress pdf online free, compress pdf boloforms, compress pdf tool, compress pdf tool online, compres pdf boloforms, compress pdf tool free, compress pdf tool online free, compress pdf tool boloforms, compress pdf tool boloforms online, compress pdf tool boloforms free, compress pdf tool boloforms online free, compress pdf tool boloforms free online, compress pdf tool boloforms free trial, compress pdf tool boloforms free download, compress pdf tool boloforms free download full version, compress pdf tool boloforms free download for windows 10, compress pdf tool boloforms free download for windows 7, compress pdf tool boloforms free download for windows 8, compress pdf tool boloforms free download for windows 8.1, compress pdf tool boloforms free download for windows xp"
        />
        <meta name="author" content="BoloForms" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link
          rel="canonical"
          href="https://www.bolosign.com/signature/pdf-tools/compress-pdf"
        />
        <meta property="og:title" content="Compress PDF Tool - BoloForms" />
        <meta
          property="og:description"
          content="Compress PDF Tool allows you to reduce the size of your PDF documents without losing quality."
        />
        <meta
          property="og:url"
          content="https://www.bolosign.com/signature/pdf-tools/compress-pdf"
        />
        <meta property="og:site_name" content="BoloForms" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="BoloForms" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@BoloForms" />
        <meta name="twitter:creator" content="@BoloForms" />
        <meta name="twitter:title" content="Compress PDF Tool - BoloForms" />
        <meta
          name="twitter:description"
          content="Compress PDF Tool allows you to reduce the size of your PDF documents without losing quality."
        />
        <link
          rel="canonical"
          href="https://www.bolosign.com/signature/pdf-tools/compress-pdf"
        />
      </Head>

      <div className="w-full">
        <UploadFile
          heading={`Compress PDF Files with BoloSign for Free`}
          description={`Want to reduce the size of your PDF without sacrificing quality or format? We have a solution for you—use our PDF compress tool. Upload your file and let BoloSign's PDF compression services compress your PDF.`}
          bgColor={"bg-[#f5daf6]"}
          btnColor={"bg-[#d69eed]"}
          image={"compress-pdf.png"}
          handleFileUpload={handleFileUpload}
          multipleFiles={false}
        />
        <span className="resolution-field"></span>
        {file && (
          <div id="resolution-field" className="my-10">
            <div className="text-center text-gray-900 text-xl mb-3">
              Set Resolution for <br />
              <span className="text-lg">{`"${file.name}"`}</span>
            </div>
            <div className="w-fit mx-auto flex justify-center flex-wrap mb-1 relative">
              {resolutions.map((res, index) => (
                <div
                  key={index}
                  onClick={() => setResolution(res)}
                  className={`w-[80px] text-center py-2 px-2 cursor-pointer hover:opacity-90 ${
                    resolution === res
                      ? "text-violet-500 border border-violet-400 font-bold"
                      : "text-gray-500 border border-gray-400"
                  }`}
                >
                  {res}
                </div>
              ))}
            </div>
            {error && (
              <p className="text-red-500 text-center text-xl">{error}</p>
            )}
            <div className="w-full mt-10">
              <button
                onClick={compressPdf}
                className="bg-[#f5daf6] mx-auto w-full sm:w-[350px] py-4 text-xl rounded-lg font-semibold text-gray-900 flex items-center justify-center gap-4 hover:opacity-90 border-2 border-gray-900"
              >
                {downloading ? (
                  <div
                    className={`flex items-center text-gray-900 space-x-2 ${styles.loader}`}
                  >
                    <div className="">Compressing</div>
                    <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce"></div>
                  </div>
                ) : (
                  <>
                    Compress PDF <FaFileZipper />
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        <div className="w-full px-4 sm:px-8">
          <Steps
            heading={"How do you reduce the size of PDF with BoloSign?"}
            steps={steps}
          />
        </div>

        <div className="w-full max-w-screen-xl mx-auto px-4 sm:px-8 mt-16 sm:mt-20">
          <WhyChooseBolosign
            heading={"Why Choose BoloSign for Compressing PDFs?"}
            description={`Our free compressor tool has tons of handy features to make your PDF files smaller. You can do it in a very quick and reliable manner ensuring that your documents are ready when you need them. Also, we prioritize your privacy and data protection by processing and storing your documents securely.`}
            reasons={reasons}
          />
        </div>

        <div className="w-full max-w-screen-xl mx-auto px-4 sm:px-8 mt-16 sm:mt-32">
          <PricingCards
            heading={"Looking for more than PDF tools?"}
            description={`Join 50,000+ teams who are already signing and accelerating their document workflow with BoloSign.`}
          />
        </div>

        <div className="w-full max-w-screen-xl mx-auto px-4 sm:px-8 mt-16 sm:mt-32">
          <TryBolosign
            heading={"Try BoloSign Free for 7 Days!"}
            bgColor={"bg-[#f5daf6]"}
          />
        </div>

        <div className="w-full px-4 sm:px-8 mt-16 sm:mt-32">
          <OtherPdfTools
            heading={`Other PDF Editing Tools`}
            description={`Free online PDF tools to merge, split, rotate, compress, convert, and many more.
Manage your daily PDF tasks with our online PDF tools.`}
          />
        </div>

        {signaturePopup && (
          <SignaturePopup
            onPopupClose={() => setSignaturePopup(false)}
            source="MERGE_PDF"
          />
        )}

        <div className="w-full max-w-screen-xl mx-auto px-4 sm:px-8 mt-16 sm:mt-32 flex flex-col gap-12">
          <h2 className="text-center font-bold leading-[45px] sm:leading-[55px] lg:leading-[70px] text-[36px] sm:text-[48px] lg:text-[60px]">
            Frequently Asked Questions
          </h2>
          <div className="max-w-screen-lg mx-auto w-full flex flex-col gap-6">
            {faqData.map((faq, index) => (
              <FaqCard
                key={index}
                faqQuestion={faq.faqQuestion}
                faqAnswer={faq.faqAnswer}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CompressPdf;
